import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Banknote, CheckCircle2, CreditCard, ExternalLink, FileClock, Loader2, QrCode, ReceiptText, RefreshCw, ShieldCheck, Users, WalletCards, X } from "lucide-react";
import { fetchAddonSummary } from "../../services/addonRequestApi";
import { cancelSubscriptionBilling, createAddonPaymentCheckout, createManualSubscriptionPayment, fetchBillingHistory, fetchSubscriptionBilling, recordBillingPaymentReturn, setupSubscriptionBilling } from "../../services/subscriptionBillingApi";
import { showErrorToast, showSuccessToast, showWarnToast } from "../../utils/toast";
import { socket } from "../../services/socket";
import ConfirmationModal from "./ConfirmationModal";
import ReactSelectField from "./ReactSelectField";

const DEFAULT_SUBSCRIPTION_SETTINGS = {
  base_monthly_amount: 1699,
  additional_user_monthly_amount: 150,
  included_users_per_active_branch: 2,
};

const HISTORY_STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const formatCurrency = (value) => `₱${Number(value || 0).toLocaleString()}`;
const formatRoundedCurrency = (value) => formatCurrency(Math.round(Number(value || 0)));
const formatPercent = (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const money = (value) => Number(Number(value || 0).toFixed(2));
const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Local timezone";

const getMethodServiceCharge = (baseAmount, method) => {
  const rate = Number(method?.service_charge_rate || 0);
  const minimumAmount = Number(method?.service_charge_minimum_amount || 0);
  const percentCharge = money(Number(baseAmount || 0) * (Number.isFinite(rate) ? rate : 0) / 100);
  const charge = money(Math.max(percentCharge, Number.isFinite(minimumAmount) ? minimumAmount : 0));
  return {
    rate: Number.isFinite(rate) ? rate : 0,
    minimumAmount: Number.isFinite(minimumAmount) ? minimumAmount : 0,
    charge,
    total: money(Number(baseAmount || 0) + charge),
  };
};

const calculateYearlyBilling = (baseMonthlyAmount, addonMonthlyTotal, packageSettings) => {
  const discountType = packageSettings?.yearly_discount_type || "PERCENTAGE";
  const discountValue = Number(packageSettings?.yearly_discount_value || 0);
  const baseMonthly = Number(baseMonthlyAmount || 0);
  const addonMonthly = Number(addonMonthlyTotal || 0);
  const discountedBaseMonthly = discountType === "FIXED"
    ? Math.max(baseMonthly - discountValue, 0)
    : Math.max(baseMonthly - (baseMonthly * Math.min(discountValue, 100) / 100), 0);
  const discountedBaseYearly = discountedBaseMonthly * 12;
  const addonYearlyTotal = addonMonthly * 12;
  const yearlyTotal = discountedBaseYearly + addonYearlyTotal;
  const regularYearlyTotal = (baseMonthly + addonMonthly) * 12;

  return {
    discountType,
    discountValue,
    discountedBaseMonthly,
    discountedBaseYearly,
    addonYearlyTotal,
    yearlyTotal,
    regularYearlyTotal,
    savings: Math.max(regularYearlyTotal - yearlyTotal, 0),
  };
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-semibold text-slate-900">{value}</span>
  </div>
);

const ManualMethodPicker = ({ methods, value, onChange }) => (
  <div className={`mt-4 grid grid-cols-2 gap-2 ${methods.length > 2 ? "sm:grid-cols-3" : ""}`}>
    {methods.map((method) => {
      const selected = value === method.key;
      const Icon = method.key === "qrph" ? QrCode : method.key === "card" ? CreditCard : WalletCards;
      return (
        <button
          key={method.key}
          type="button"
          onClick={() => onChange(method.key)}
          className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-semibold transition ${selected ? "border-transparent text-white shadow-sm" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
          style={selected ? { background: "var(--color-primary)" } : undefined}
        >
          <Icon className="h-4 w-4" />
          {method.key === "card" ? "Debit / Credit Card" : method.label}
        </button>
      );
    })}
  </div>
);

const parseDateOnly = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDate = (value) => {
  const date = parseDateOnly(value);
  if (!date) return "Not set";
  return date.toLocaleDateString("en-PH", {
    timeZone: localTimeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getBillingHistoryNote = (row) => {
  const status = String(row?.status || "").toUpperCase();
  if (status !== "PAID") {
    if (status === "PENDING") return "Awaiting payment completion";
    if (status === "CANCELLED" || status === "CANCELED") return "Checkout was cancelled";
    if (status === "EXPIRED") return "Checkout expired";
    if (status === "FAILED") return "Payment failed";
    return null;
  }

  if (row?.service_start_date && row?.service_end_date) {
    return `Covers ${formatDate(row.service_start_date)} - ${formatDate(row.service_end_date)}`;
  }

  if (!row?.paid_at) return null;
  const date = new Date(row.paid_at);
  if (Number.isNaN(date.getTime())) return null;

  if (row?.billing_cycle === "PRORATED") return "Covers prorated add-on period";
  const month = date.toLocaleDateString("en-PH", {
    timeZone: localTimeZone,
    month: "long",
    year: "numeric",
  });
  return row?.billing_cycle === "YEARLY" ? `Covers yearly billing from ${month}` : `Covers ${month}`;
};

const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getRenewalDate = (value) => {
  const date = parseDateOnly(value);
  if (!date) return null;
  date.setDate(date.getDate() + 1);
  return toLocalDateKey(date);
};

const getBillingPeriodStatus = (endDate) => {
  const endDateKey = endDate ? String(endDate).slice(0, 10) : "";
  if (!parseDateOnly(endDateKey)) return null;

  const todayKey = toLocalDateKey(new Date());
  if (todayKey > endDateKey) {
    return {
      label: `Expired ${formatDate(endDateKey)}`,
      className: "border-rose-100 bg-rose-50 text-rose-700",
    };
  }

  return {
    label: `Renews ${formatDate(getRenewalDate(endDateKey))}`,
    className: "border-emerald-100 bg-emerald-50 text-emerald-700",
  };
};

export default function BillingModal({ open, onClose, customer = null, forceRenewal = false, initialSection = "BILLING", returnNotice = null }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("MONTHLY");
  const [autoPay, setAutoPay] = useState(null);
  const [autoPayLoading, setAutoPayLoading] = useState(false);
  const [autoPayAccepted, setAutoPayAccepted] = useState(false);
  const [paymentMode, setPaymentMode] = useState("MANUAL");
  const [manualPaymentLoading, setManualPaymentLoading] = useState(false);
  const [manualPaymentMethod, setManualPaymentMethod] = useState("gcash");
  const [gcashCheckout, setGcashCheckout] = useState(null);

  useEffect(() => {
    setGcashCheckout(null);
  }, [open, manualPaymentMethod, billingCycle]);
  const [showCancelAutoPay, setShowCancelAutoPay] = useState(false);
  const [activeSection, setActiveSection] = useState("BILLING");
  const [addonCheckoutId, setAddonCheckoutId] = useState(null);
  const [history, setHistory] = useState({ rows: [], pagination: { page: 1, total: 0, totalPages: 1 } });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState("");
  const handledReturnNoticeRef = useRef(null);

  useEffect(() => {
    setAutoPayAccepted(false);
  }, [open, billingCycle, autoPay?.quote?.amount]);

  const selectedHistoryStatus = useMemo(
    () => HISTORY_STATUS_OPTIONS.find((option) => option.value === historyStatusFilter) || HISTORY_STATUS_OPTIONS[0],
    [historyStatusFilter]
  );

  useEffect(() => {
    const methods = Array.isArray(autoPay?.manual_payment_methods) ? autoPay.manual_payment_methods : [];
    if (methods.length && !methods.some((method) => method.key === manualPaymentMethod)) {
      setManualPaymentMethod(methods[0].key);
    }
  }, [autoPay?.manual_payment_methods, manualPaymentMethod]);

  useEffect(() => {
    if (!open) return;

    setPaymentMode("MANUAL");
    setActiveSection(initialSection || "BILLING");

    let isMounted = true;
    const loadBilling = async () => {
      setLoading(true);
      try {
        const [summaryResponse, autoPayResponse] = await Promise.all([
          fetchAddonSummary(),
          fetchSubscriptionBilling("MONTHLY"),
        ]);
        if (isMounted) {
          setSummary(summaryResponse.data || null);
          setAutoPay(autoPayResponse.data || null);
        }
      } catch (error) {
        console.error("Failed to load billing details:", error);
        showErrorToast("Failed to load billing details.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadBilling();
    return () => {
      isMounted = false;
    };
  }, [initialSection, open]);

  useEffect(() => {
    if (!open || !returnNotice?.id || handledReturnNoticeRef.current === returnNotice.id) return;

    handledReturnNoticeRef.current = returnNotice.id;
    let mounted = true;

    const normalizedStatus = String(returnNotice.providerStatus || returnNotice.subscriptionStatus || "").toLowerCase();
    const paymentLabel = returnNotice.paymentType === "addon" ? "Add-on payment" : "Subscription payment";
    const successStatuses = ["completed", "complete", "paid", "success", "succeeded"];
    const failedStatuses = ["cancelled", "canceled", "failed", "expired"];

    const refreshReturnBilling = async () => {
      const recurringReturn = returnNotice.paymentType === "recurring";
      setActiveSection(recurringReturn ? "BILLING" : "HISTORY");
      if (recurringReturn) setPaymentMode("AUTO");
      setHistoryLoading(true);
      try {
        const returnResponse = await recordBillingPaymentReturn(returnNotice.paymentType || "manual", normalizedStatus);
        const reconciledStatus = String(returnResponse.data?.status || "").toLowerCase();

        const [summaryResponse, billingResponse, historyResponse] = await Promise.all([
          fetchAddonSummary(),
          fetchSubscriptionBilling(billingCycle),
          fetchBillingHistory(1, 10, historyStatusFilter),
        ]);
        if (!mounted) return;

        const nextHistory = historyResponse.data || { rows: [], pagination: { page: 1, total: 0, totalPages: 1 } };
        const latestHistory = Array.isArray(nextHistory.rows) ? nextHistory.rows[0] : null;
        const latestStatus = String(latestHistory?.status || "").toUpperCase();
        const latestPaid = latestStatus === "PAID";
        const latestTerminal = ["CANCELLED", "CANCELED", "FAILED", "EXPIRED"].includes(latestStatus);

        setSummary(summaryResponse.data || null);
        setAutoPay(billingResponse.data || null);
        setHistory(nextHistory);

        if (recurringReturn) {
          if (reconciledStatus === "active") showSuccessToast("Card auto-pay is authorized. Charges will follow your billing schedule.");
          else if (["cancelled", "canceled", "expired", "paused", "past_due"].includes(reconciledStatus)) showWarnToast("Auto-pay is not active. Review your agreement below.");
          else showWarnToast("Card authorization is pending. Continue setup when ready.");
        } else if (successStatuses.includes(normalizedStatus) || latestPaid) {
          showSuccessToast(`${paymentLabel} received. Billing history has been updated.`);
        } else if (failedStatuses.includes(normalizedStatus) || failedStatuses.includes(reconciledStatus) || latestTerminal) {
          showErrorToast(`${paymentLabel} was not completed. You can try again when ready.`);
        } else {
          showWarnToast(`${paymentLabel} is being verified. Billing history will update once HitPay confirms it.`);
        }
      } catch (error) {
        console.error("Unable to refresh billing after HitPay return:", error);
        showErrorToast("Unable to refresh billing after payment return.");
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    };

    refreshReturnBilling();
    return () => {
      mounted = false;
    };
  }, [billingCycle, historyStatusFilter, open, returnNotice]);

  useEffect(() => {
    if (!open || loading) return;
    let mounted = true;
    fetchSubscriptionBilling(billingCycle)
      .then((response) => mounted && setAutoPay(response.data || null))
      .catch(() => {});
    return () => { mounted = false; };
  }, [billingCycle, loading, open]);

  useEffect(() => {
    if (!open || activeSection !== "HISTORY") return;
    let mounted = true;
    setHistoryLoading(true);
    fetchBillingHistory(history.pagination.page || 1, history.pagination.pageSize || 10, historyStatusFilter)
      .then((response) => mounted && setHistory(response.data || { rows: [], pagination: { page: 1, total: 0, totalPages: 1 } }))
      .catch((error) => showErrorToast(error.response?.data?.message || "Unable to load billing history."))
      .finally(() => mounted && setHistoryLoading(false));
    return () => { mounted = false; };
  }, [activeSection, history.pagination.page, history.pagination.pageSize, historyStatusFilter, open]);

  useEffect(() => {
    if (!open) return undefined;

    let mounted = true;
    const refreshVisibleBilling = async (event = null) => {
      try {
        const requests = [
          fetchAddonSummary(),
          fetchSubscriptionBilling(billingCycle),
        ];
        if (activeSection === "HISTORY") {
          requests.push(fetchBillingHistory(history.pagination.page || 1, history.pagination.pageSize || 10, historyStatusFilter));
        }

        const [summaryResponse, billingResponse, historyResponse] = await Promise.all(requests);
        if (!mounted) return;

        setSummary(summaryResponse.data || null);
        setAutoPay(billingResponse.data || null);
        if (historyResponse) {
          setHistory(historyResponse.data || { rows: [], pagination: { page: 1, total: 0, totalPages: 1 } });
        }

        if (event?.status === "PAID" && event?.newly_confirmed) {
          showSuccessToast(event.message || "Payment confirmed. Your billing details are now updated.");
        }
      } catch (error) {
        console.error("Unable to refresh live billing details:", error);
      }
    };

    const handleBillingUpdate = (event) => refreshVisibleBilling(event);
    const handleWindowFocus = () => refreshVisibleBilling();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshVisibleBilling();
    };

    socket.on("subscription_billing_updated", handleBillingUpdate);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mounted = false;
      socket.off("subscription_billing_updated", handleBillingUpdate);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeSection, billingCycle, history.pagination.page, history.pagination.pageSize, historyStatusFilter, open]);

  const handleAutoPay = async () => {
    if (!autoPayAccepted) return;
    setAutoPayLoading(true);
    try {
      const response = await setupSubscriptionBilling(billingCycle, "card", autoPayAccepted);
      const data = response.data || {};
      setAutoPay(data);
      setAutoPayAccepted(false);
      if (data.checkout_url && data.billing?.status !== "ACTIVE") {
        window.location.assign(data.checkout_url);
        return;
      }
      showSuccessToast(data.message || "Card auto-pay updated.");
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Unable to set up card auto-pay.");
    } finally {
      setAutoPayLoading(false);
    }
  };

  const handleManualPayment = async () => {
    setManualPaymentLoading(true);
    try {
      const response = await createManualSubscriptionPayment(billingCycle, manualPaymentMethod);
      if (response.data?.already_paid) {
        showSuccessToast(response.data.message || "Subscription payment confirmed. Your billing details are now updated.");
        const [summaryResponse, billingResponse, historyResponse] = await Promise.all([
          fetchAddonSummary(),
          fetchSubscriptionBilling(billingCycle),
          fetchBillingHistory(1, 10, historyStatusFilter),
        ]);
        setSummary(summaryResponse.data || null);
        setAutoPay(billingResponse.data || null);
        setHistory(historyResponse.data || { rows: [], pagination: { page: 1, total: 0, totalPages: 1 } });
        setActiveSection("HISTORY");
        return;
      }
      const checkoutUrl = response.data?.checkout_url;
      if (!checkoutUrl) throw new Error("HitPay did not return a checkout URL.");
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      if (manualPaymentMethod === "gcash") setGcashCheckout(response.data);
      window.location.assign(isMobile && response.data?.app_url ? response.data.app_url : checkoutUrl);
    } catch (error) {
      showErrorToast(error.response?.data?.message || error.message || "Unable to start the manual payment.");
    } finally {
      setManualPaymentLoading(false);
    }
  };

  const handleCancelAutoPay = async () => {
    setAutoPayLoading(true);
    try {
      const response = await cancelSubscriptionBilling();
      showSuccessToast(response.data?.message || "Card auto-pay cancelled.");
      setShowCancelAutoPay(false);
      const refreshed = await fetchSubscriptionBilling(billingCycle);
      setAutoPay(refreshed.data || null);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Unable to cancel card auto-pay.");
    } finally {
      setAutoPayLoading(false);
    }
  };

  const handleAddonPayment = async (request) => {
    setAddonCheckoutId(request.id);
    try {
      const response = await createAddonPaymentCheckout(request.id, manualPaymentMethod);
      const checkoutUrl = response.data?.checkout_url;
      if (!checkoutUrl) throw new Error("HitPay did not return a checkout URL.");
      window.location.assign(checkoutUrl);
    } catch (error) {
      showErrorToast(error.response?.data?.message || error.message || "Unable to start the add-on payment.");
    } finally {
      setAddonCheckoutId(null);
    }
  };

  const subscriptionSettings = summary?.subscriptionSettings || DEFAULT_SUBSCRIPTION_SETTINGS;
  const manualPaymentMethods = Array.isArray(autoPay?.manual_payment_methods) ? autoPay.manual_payment_methods : [];
  const selectedManualPaymentMethod = manualPaymentMethods.find((method) => method.key === manualPaymentMethod) || null;
  const baseMonthlyAmount = Number(subscriptionSettings.base_monthly_amount || DEFAULT_SUBSCRIPTION_SETTINGS.base_monthly_amount);
  const addonPrice = Number(subscriptionSettings.additional_user_monthly_amount || DEFAULT_SUBSCRIPTION_SETTINGS.additional_user_monthly_amount);
  const approvedUsers = Number(summary?.approvedAdditionalUsers || 0);
  const pendingUsers = Number(summary?.pendingAdditionalUsers || 0);
  const approvedAddonMonthlyTotal = Number(summary?.approvedAddonMonthlyTotal || 0);
  const currentMonthlyTotal = baseMonthlyAmount + approvedAddonMonthlyTotal;
  const yearlyBilling = calculateYearlyBilling(baseMonthlyAmount, approvedAddonMonthlyTotal, subscriptionSettings);
  const yearlyEnabled = Boolean(subscriptionSettings.yearly_enabled);
  const billingSubtotal = billingCycle === "YEARLY" && yearlyEnabled ? yearlyBilling.yearlyTotal : currentMonthlyTotal;
  const payableTotal = billingSubtotal;
  const manualServiceCharge = getMethodServiceCharge(payableTotal, selectedManualPaymentMethod);
  const displayTotalDue = paymentMode === "MANUAL" ? manualServiceCharge.total : payableTotal;
  const recurringChargeAmount = Number(autoPay?.quote?.amount ?? payableTotal);
  const subscriptionStartDate = customer?.subscription_start_date;
  const subscriptionEndDate = customer?.subscription_end_date;
  const billingPeriodStatus = getBillingPeriodStatus(subscriptionEndDate);
  const hasApprovedAddons = approvedUsers > 0 || approvedAddonMonthlyTotal > 0;
  const hasPendingAddons = pendingUsers > 0;
  const billingRows = [
    { label: "Base package", value: formatCurrency(baseMonthlyAmount) },
    ...(hasApprovedAddons
      ? [
          { label: "Approved add-ons", value: `${approvedUsers} user${approvedUsers === 1 ? "" : "s"}` },
          { label: "Add-on total", value: formatCurrency(approvedAddonMonthlyTotal) },
        ]
      : []),
    ...(hasApprovedAddons || hasPendingAddons
      ? [{ label: "Add-on rate", value: `${formatCurrency(addonPrice)}/user` }]
      : []),
    ...(hasPendingAddons ? [{ label: "Pending requests", value: pendingUsers }] : []),
  ];

  useEffect(() => {
    if (!yearlyEnabled) {
      setBillingCycle("MONTHLY");
    }
  }, [yearlyEnabled]);

  const latestApprovedRequests = useMemo(
    () => (summary?.latestRequests || []).filter((request) => request.status === "APPROVED" && ["NOT_REQUIRED", "PAID"].includes(request.payment_status || "NOT_REQUIRED")),
    [summary?.latestRequests]
  );
  const addonPaymentRequests = useMemo(
    () => (summary?.latestRequests || []).filter((request) => request.status === "APPROVED" && request.payment_status !== "NOT_REQUIRED"),
    [summary?.latestRequests]
  );
  const unpaidAddonCount = addonPaymentRequests.filter((request) => ["PAYMENT_REQUIRED", "PENDING", "FAILED"].includes(request.payment_status)).length;

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-modal-back="true"
      className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-slate-950/45 px-3 py-3 backdrop-blur-md sm:px-4 sm:py-6"
    >
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)] sm:max-h-[calc(100dvh-3rem)]">
        <div className="relative shrink-0 overflow-hidden border-b border-slate-200/80 px-5 py-5 sm:px-6">
          <div
            className="pointer-events-none absolute inset-0 opacity-80"
            style={{
              background: "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 12%, white), transparent 68%)",
            }}
          />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
              {forceRenewal ? <AlertTriangle className="h-6 w-6" /> : <ReceiptText className="h-6 w-6" />}
              </div>
              <div className="min-w-0">
                <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                  {forceRenewal ? "Subscription Renewal Required" : "Billing"}
                </h2>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                  {forceRenewal
                    ? "Your subscription has expired. Choose a payment method and renew to continue using the POS."
                    : "Review subscription charges and choose a one-time payment or recurring card payments."}
                </p>
              </div>
            </div>
            {!forceRenewal && (
              <button type="button" data-modal-close onClick={onClose} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/80 bg-white/90 text-slate-500 shadow-sm transition hover:bg-white hover:text-slate-800" aria-label="Close billing">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto bg-slate-50/80 px-4 py-4 pb-7 sm:px-6 sm:py-5 sm:pb-6">
          {forceRenewal && (
            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">Access is paused until renewal is completed.</p>
                  <p className="mt-1 text-amber-700">
                    This billing window will stay visible and reopen automatically until the subscription period is updated.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="mb-4 grid grid-cols-3 gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1">
            {[
              { id: "BILLING", label: "Current Bill", icon: ReceiptText },
              { id: "ADDONS", label: "Add-on Payments", icon: Users, badge: unpaidAddonCount },
              { id: "HISTORY", label: "Billing History", icon: FileClock },
            ].map((item) => {
              const Icon = item.icon;
              const selected = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSection(item.id)}
                  className={`relative inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-2 text-xs font-semibold transition sm:text-sm ${selected ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:bg-white/60 hover:text-slate-800"}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {item.badge > 0 && <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-100 px-1 text-[10px] font-bold text-rose-700">{item.badge}</span>}
                </button>
              );
            })}
          </div>
          <div className={`${activeSection === "BILLING" ? "grid" : "hidden"} gap-4 transition duration-200 lg:grid-cols-[0.92fr_1.08fr] ${loading ? "pointer-events-none opacity-35 blur-[1px]" : "opacity-100 blur-0"}`}>
              <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-900">Current Billing</h3>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">Monthly</span>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Billing Period</p>
                        <p className="mt-1 text-base font-semibold leading-6 text-slate-900">
                          {formatDate(subscriptionStartDate)} - {formatDate(subscriptionEndDate)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Based on {localTimeZone}
                        </p>
                      </div>
                      {billingPeriodStatus && (
                        <span className={`w-fit shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${billingPeriodStatus.className}`}>
                          {billingPeriodStatus.label}
                        </span>
                      )}
                    </div>
                </div>

                <div className="mt-4 space-y-3">
                  {billingRows.map((row) => (
                    <DetailRow key={row.label} label={row.label} value={row.value} />
                  ))}
                </div>

                <div className="mt-5 grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setBillingCycle("MONTHLY")}
                    className={`rounded-xl px-3 py-3 text-left transition ${billingCycle === "MONTHLY" ? "bg-white shadow-sm ring-1 ring-slate-200" : "hover:bg-white/70"}`}
                  >
                    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Monthly</span>
                    <span className="mt-1 block text-sm font-semibold text-slate-900">{formatCurrency(currentMonthlyTotal)}</span>
                    <span className="mt-1 block text-xs text-slate-500">Subscription total</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => yearlyEnabled && setBillingCycle("YEARLY")}
                    disabled={!yearlyEnabled}
                    className={`rounded-xl px-3 py-3 text-left transition ${
                      billingCycle === "YEARLY" && yearlyEnabled
                        ? "bg-white shadow-sm ring-1 ring-[var(--color-primary)]"
                        : yearlyEnabled
                          ? "hover:bg-white/70"
                          : "cursor-not-allowed bg-slate-100 opacity-70"
                    }`}
                  >
                    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Yearly</span>
                    <span className="mt-1 block text-sm font-semibold text-slate-900">
                      {yearlyEnabled ? formatRoundedCurrency(yearlyBilling.yearlyTotal) : "Not offered"}
                    </span>
                    <span className={`mt-1 block text-xs ${yearlyEnabled ? "text-emerald-600" : "text-slate-500"}`}>
                      {yearlyEnabled ? `Save ${formatRoundedCurrency(yearlyBilling.savings)}` : "Enable yearly in Subscription Packages"}
                    </span>
                  </button>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-950 p-4 text-white shadow-sm">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/50">Total Due</p>
                      <p className="mt-1 text-sm text-white/70">{billingCycle === "YEARLY" && yearlyEnabled ? "Yearly payment" : "Monthly payment"}</p>
                    </div>
                    <p className="text-3xl font-bold tracking-tight text-white">{billingCycle === "YEARLY" && yearlyEnabled ? formatRoundedCurrency(displayTotalDue) : formatCurrency(displayTotalDue)}</p>
                  </div>
                  <div className="mt-4 space-y-2 border-t border-white/10 pt-3 text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-white/65">Subscription subtotal</span>
                      <span className="font-semibold text-white/85">{billingCycle === "YEARLY" && yearlyEnabled ? formatRoundedCurrency(billingSubtotal) : formatCurrency(billingSubtotal)}</span>
                    </div>
                    {paymentMode === "MANUAL" && manualServiceCharge.charge > 0 && (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/65">
                          Service charge ({selectedManualPaymentMethod?.label || "Online"} {formatPercent(manualServiceCharge.rate)}{manualServiceCharge.minimumAmount > 0 ? ` or ${formatCurrency(manualServiceCharge.minimumAmount)} minimum` : ""})
                        </span>
                        <span className="font-semibold text-white/85">{formatCurrency(manualServiceCharge.charge)}</span>
                      </div>
                    )}
                  </div>
                  {billingCycle === "YEARLY" && yearlyEnabled && (
                    <p className="mt-3 text-xs text-white/60">
                      Base package discounted to {formatRoundedCurrency(yearlyBilling.discountedBaseYearly)}/year
                      {yearlyBilling.addonYearlyTotal > 0 ? ` plus ${formatRoundedCurrency(yearlyBilling.addonYearlyTotal)}/year add-ons` : ""}
                      {" "}with {yearlyBilling.discountType === "FIXED"
                        ? `${formatCurrency(yearlyBilling.discountValue)} base monthly discount`
                        : `${yearlyBilling.discountValue}% discount`}
                      .
                    </p>
                  )}
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Current Add-ons</p>
                  {latestApprovedRequests.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {latestApprovedRequests.map((request) => (
                        <div key={request.id} className="flex items-center justify-between gap-3 text-xs">
                          <span className="text-slate-500">{request.quantity} additional user{request.quantity === 1 ? "" : "s"}</span>
                          <span className="font-semibold text-slate-800">{formatCurrency(request.addon_monthly_amount)}/month</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500">No approved add-ons yet.</p>
                  )}
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="grid grid-cols-2 gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setPaymentMode("MANUAL")}
                    className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold transition ${paymentMode === "MANUAL" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    <Banknote className="h-4 w-4" />
                    Pay Manually
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMode("AUTO")}
                    className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold transition ${paymentMode === "AUTO" ? "text-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                    style={paymentMode === "AUTO" ? { background: "var(--color-primary)" } : undefined}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Auto-pay
                  </button>
                </div>

                {paymentMode === "MANUAL" ? (
                  <div className="mt-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">One-time subscription payment</h3>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          {autoPay?.billing?.status === "ACTIVE"
                            ? "Pay this billing period once. Your existing auto-pay remains active until you cancel it from the Auto-pay tab."
                            : "Pay only this billing period. No card is saved and automatic renewal stays off."}
                        </p>
                      </div>
                      <Banknote className="h-5 w-5 shrink-0 text-slate-400" />
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Amount to pay</p>
                          <p className="mt-1 text-2xl font-semibold text-slate-950">{billingCycle === "YEARLY" ? formatRoundedCurrency(manualServiceCharge.total) : formatCurrency(manualServiceCharge.total)}</p>
                          {manualServiceCharge.charge > 0 && (
                            <p className="mt-1 text-xs font-semibold text-slate-500">
                              Includes {formatCurrency(manualServiceCharge.charge)} service charge
                            </p>
                          )}
                        </div>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">
                          {billingCycle === "YEARLY" ? "Yearly" : "Monthly"}
                        </span>
                      </div>
                    </div>

                    {manualPaymentMethods.length > 0 && (
                      <ManualMethodPicker methods={manualPaymentMethods} value={manualPaymentMethod} onChange={setManualPaymentMethod} />
                    )}

                    <div className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                      <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                      <p className="text-xs leading-5 text-emerald-800">
                        {manualPaymentMethod === "gcash"
                          ? "Continue to GCash to approve your payment. On a supported phone, open the GCash app when prompted."
                          : manualPaymentMethod === "card"
                          ? "Enter your debit or credit card details on HitPay's secure payment page. Your bank may ask you to approve the payment."
                          : `You will continue to HitPay's secure ${selectedManualPaymentMethod?.label || "online"} checkout.`}
                        {" "}This is a one-time payment and does not create an auto-pay agreement.
                      </p>
                    </div>

                    {!autoPay?.configured && (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                        Online subscription payment is not available yet. Please contact ZeroOne support.
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleManualPayment}
                      disabled={manualPaymentLoading || !autoPay?.configured || manualPaymentMethods.length === 0}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                      style={!manualPaymentLoading && autoPay?.configured && manualPaymentMethods.length > 0 ? { background: "var(--color-primary)" } : undefined}
                    >
                      {manualPaymentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                      {manualPaymentLoading ? "Preparing checkout..." : manualPaymentMethod === "gcash" ? "Continue to GCash" : manualPaymentMethod === "card" ? "Enter card details" : "Continue to One-time Payment"}
                    </button>
                    {manualPaymentMethod === "gcash" && gcashCheckout && (
                      <div className="mt-3 text-xs leading-5 text-slate-600">
                        <p>If GCash did not open, continue with the same payment below.</p>
                        <div className="mt-2 flex flex-wrap gap-4">
                          {gcashCheckout.app_url && <a className="font-semibold underline" href={gcashCheckout.app_url}>Open GCash app</a>}
                          <a className="font-semibold underline" href={gcashCheckout.checkout_url}>Continue in browser</a>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Subscription Auto-pay</h3>
                    <p className="mt-1 text-xs text-slate-500">Authorize your credit or debit card for automatic {billingCycle === "YEARLY" ? "yearly" : "monthly"} renewals.</p>
                  </div>
                  <CreditCard className="h-5 w-5 text-slate-400" />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2" aria-label="Auto-pay methods">
                  <div className="flex min-h-12 items-center justify-center gap-2 rounded-xl border px-3 text-center text-sm font-semibold text-white" style={{ background: "var(--color-primary)" }}>
                    <CreditCard className="h-4 w-4 shrink-0" /> Debit / Credit Card
                  </div>
                  <button type="button" disabled className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-400">
                    <WalletCards className="h-4 w-4" /> GCash unavailable
                  </button>
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-500">GCash auto-pay is currently unsupported by HitPay. You can still use GCash under Pay Manually each billing period.</p>

                <div className={`mt-4 rounded-2xl border p-4 ${autoPay?.billing?.status === "ACTIVE" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[var(--color-primary)] shadow-sm">
                      {autoPay?.billing?.status === "ACTIVE" ? <CheckCircle2 className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">Card Auto-pay</p>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${autoPay?.billing?.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : autoPay?.billing?.status === "PENDING" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>
                          {autoPay?.billing?.status || "NOT SET UP"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs leading-5 text-slate-600">Eligible Visa or Mastercard debit and credit cards through HitPay. Your bank must allow recurring online payments. Card details are entered and stored securely by HitPay.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Next charge</p>
                    <p className="mt-1 text-base font-semibold text-slate-900">{billingCycle === "YEARLY" ? formatRoundedCurrency(recurringChargeAmount) : formatCurrency(recurringChargeAmount)}</p>
                    <p className="mt-1 text-xs text-slate-500">{billingCycle === "YEARLY" ? "Every year" : "Every month"}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Renewal date</p>
                    <p className="mt-1 text-base font-semibold text-slate-900">{formatDate(autoPay?.billing?.next_billing_date || autoPay?.auto_pay_start_date)}</p>
                    <p className="mt-1 text-xs text-slate-500">{autoPay?.billing?.last_charged_at ? "Next scheduled charge" : "First charge after your current paid period, or today if expired"}</p>
                  </div>
                </div>

                {!autoPay?.auto_pay_configured && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    Card auto-pay is not available yet. Please contact ZeroOne support.
                  </div>
                )}

                {autoPay?.amount_outdated && autoPay?.billing?.status === "ACTIVE" && (
                  <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs leading-5 text-blue-800">
                    Your package or add-ons changed. Update auto-pay so the next charge uses the current bill.
                  </div>
                )}

                {autoPay?.billing?.status === "PAST_DUE" && (
                  <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">The last automatic charge failed. Your paid subscription period was not extended. Review HitPay's payment update email, or cancel this agreement before setting up a replacement.</p>
                )}

                {!(autoPay?.billing?.status === "ACTIVE" && !autoPay?.amount_outdated) && (
                  <label className="mt-4 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-600">
                    <input type="checkbox" checked={autoPayAccepted} onChange={(event) => setAutoPayAccepted(event.target.checked)} className="mt-1 h-4 w-4 shrink-0" />
                    <span>I authorize recurring charges of {formatCurrency(recurringChargeAmount)} every {billingCycle === "YEARLY" ? "year" : "month"}, starting {formatDate(autoPay?.billing?.next_billing_date || autoPay?.auto_pay_start_date)}. Auto-pay continues until cancelled. I can cancel here without losing my remaining paid access.</span>
                  </label>
                )}

                <button
                  type="button"
                  onClick={handleAutoPay}
                  disabled={autoPayLoading || !autoPayAccepted || !autoPay?.auto_pay_configured || ["PAUSED", "PAST_DUE"].includes(autoPay?.billing?.status) || (autoPay?.billing?.status === "ACTIVE" && !autoPay?.amount_outdated)}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                  style={!autoPayLoading && autoPayAccepted && autoPay?.auto_pay_configured && !["PAUSED", "PAST_DUE"].includes(autoPay?.billing?.status) && !(autoPay?.billing?.status === "ACTIVE" && !autoPay?.amount_outdated) ? { background: "var(--color-primary)" } : undefined}
                >
                  {autoPayLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : autoPay?.billing?.status === "PENDING" ? <ExternalLink className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                  {autoPayLoading ? "Please wait..." : autoPay?.billing?.status === "ACTIVE" ? (autoPay?.amount_outdated ? "Update Next Auto-pay" : "Card Auto-pay Active") : autoPay?.billing?.status === "PENDING" ? "Continue Card Authorization" : "Set Up Card Auto-pay"}
                </button>

                {["ACTIVE", "PENDING", "PAUSED", "PAST_DUE"].includes(autoPay?.billing?.status) && (
                  <button type="button" onClick={() => setShowCancelAutoPay(true)} className="mt-3 w-full rounded-2xl px-5 py-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50">
                    Cancel Card Auto-pay
                  </button>
                )}
                  </div>
                )}
              </section>
          </div>

          {activeSection === "ADDONS" && (
            <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Approved Requests</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-950">Add-on payments</h3>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">Pay only the prorated amount through the current subscription end date. Staff slots activate after payment confirmation and join the regular renewal bill afterward.</p>
                </div>
                <span className="w-fit rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">{addonPaymentRequests.length} request{addonPaymentRequests.length === 1 ? "" : "s"}</span>
              </div>

              {manualPaymentMethods.length > 0 && (
                <div className="max-w-md">
                  <ManualMethodPicker methods={manualPaymentMethods} value={manualPaymentMethod} onChange={setManualPaymentMethod} />
                </div>
              )}

              {manualPaymentMethods.length === 0 && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                  No approved online subscription payment method is available. Please contact ZeroOne support.
                </div>
              )}

              {addonPaymentRequests.length ? (
                <div className="mt-5 space-y-3">
                  {addonPaymentRequests.map((request) => {
                    const status = request.payment_status || "PAYMENT_REQUIRED";
                    const paid = status === "PAID";
                    const pending = status === "PENDING";
                    const addonBaseAmount = Number(request.prorated_subtotal || 0);
                    const savedServiceCharge = Number(request.service_charge_amount || 0);
                    const projectedServiceCharge = getMethodServiceCharge(addonBaseAmount, selectedManualPaymentMethod);
                    const serviceChargeAmount = savedServiceCharge > 0 ? savedServiceCharge : projectedServiceCharge.charge;
                    const addonCheckoutTotal = savedServiceCharge > 0
                      ? Number(request.amount_due || 0)
                      : projectedServiceCharge.total;
                    return (
                      <article key={request.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-900">{request.quantity} additional user slot{Number(request.quantity) === 1 ? "" : "s"}</p>
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${paid ? "bg-emerald-100 text-emerald-700" : pending ? "bg-amber-100 text-amber-700" : status === "FAILED" ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}>
                                {status.replaceAll("_", " ")}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-slate-500">Service period: {formatDate(request.service_start_date)} - {formatDate(request.service_end_date)}</p>
                            <p className="mt-1 text-xs text-slate-500">Renewal rate: {formatCurrency(request.addon_monthly_amount)}/month</p>
                          </div>
                          <div className="sm:text-right">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Prorated total</p>
                            <p className="mt-1 text-xl font-semibold text-slate-950">{formatCurrency(addonCheckoutTotal)}</p>
                            {serviceChargeAmount > 0 && (
                              <p className="mt-1 text-xs text-slate-500">Includes {formatCurrency(serviceChargeAmount)} service charge</p>
                            )}
                          </div>
                        </div>
                        {!paid && (
                          <button
                            type="button"
                            onClick={() => handleAddonPayment(request)}
                            disabled={addonCheckoutId === request.id || manualPaymentMethods.length === 0}
                            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-90 disabled:cursor-wait disabled:opacity-60 sm:ml-auto sm:w-auto sm:min-w-56"
                            style={{ background: "var(--color-primary)" }}
                          >
                            {addonCheckoutId === request.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                            {addonCheckoutId === request.id ? "Preparing checkout..." : pending ? "Continue Payment" : status === "FAILED" ? "Retry Payment" : "Pay Add-on"}
                          </button>
                        )}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center">
                  <Users className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-3 text-sm font-semibold text-slate-700">No add-on payments yet</p>
                  <p className="mt-1 text-xs text-slate-500">Approved add-on charges and their payment status will appear here.</p>
                </div>
              )}
            </section>
          )}

          {activeSection === "HISTORY" && (
            <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Statements</p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-950">Billing history</h3>
                  <p className="mt-1 text-sm text-slate-500">Subscription renewals and add-on payments are kept together for easier reconciliation.</p>
                </div>
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
                  <div className="w-full sm:w-44">
                    <ReactSelectField
                      options={HISTORY_STATUS_OPTIONS}
                      value={selectedHistoryStatus}
                      onChange={(option) => {
                        setHistoryStatusFilter(option?.value || "");
                        setHistory((current) => ({
                          ...current,
                          pagination: { ...(current.pagination || {}), page: 1 },
                        }));
                      }}
                      placeholder="All Status"
                      isSearchable={false}
                      ariaLabel="Filter billing history by status"
                    />
                  </div>
                  <span className="shrink-0 self-start rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 sm:self-auto">{history.pagination?.total || 0} records</span>
                </div>
              </div>

              {historyLoading ? (
                <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[var(--color-primary)]" /></div>
              ) : history.rows?.length ? (
                <div className="divide-y divide-slate-100">
                  {history.rows.map((row, index) => {
                    const historyNote = getBillingHistoryNote(row);
                    const rowStatus = String(row.status || "").toUpperCase();
                    return (
                      <div key={row.id} className="grid gap-3 px-4 py-4 sm:grid-cols-[36px_1fr_auto] sm:items-center sm:px-5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{((history.pagination.page - 1) * history.pagination.pageSize) + index + 1}</span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-slate-900">{row.description}</p>
                            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${rowStatus === "PAID" ? "bg-emerald-100 text-emerald-700" : rowStatus === "FAILED" || rowStatus === "EXPIRED" || rowStatus === "CANCELLED" || rowStatus === "CANCELED" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{rowStatus.replaceAll("_", " ")}</span>
                          </div>
                          <p className="mt-1 break-all text-xs text-slate-500">{row.invoice_number || row.reference || "Payment record pending"}</p>
                          {historyNote && <p className={`mt-1 text-xs font-semibold ${rowStatus === "PAID" ? "text-slate-600" : "text-slate-500"}`}>{historyNote}</p>}
                          <p className="mt-1 text-xs text-slate-400">{new Date(row.paid_at || row.created_at).toLocaleString("en-PH")}</p>
                        </div>
                        <div className="sm:text-right">
                          <p className="text-lg font-semibold text-slate-950">{formatCurrency(row.amount)}</p>
                          {Number(row.service_charge_amount || 0) > 0 && (
                            <p className="text-xs text-slate-500">
                              Service charge {formatCurrency(row.service_charge_amount)}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-5 py-14 text-center"><FileClock className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 text-sm font-semibold text-slate-700">{historyStatusFilter ? "No records match this status" : "No billing records yet"}</p><p className="mt-1 text-xs text-slate-500">{historyStatusFilter ? "Try another status to review other billing activity." : "Completed and pending payments will appear here."}</p></div>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 sm:px-5">
                <span>Page {history.pagination?.page || 1} of {history.pagination?.totalPages || 1}</span>
                <div className="flex gap-2">
                  <button type="button" disabled={(history.pagination?.page || 1) <= 1 || historyLoading} onClick={() => setHistory((current) => ({ ...current, pagination: { ...current.pagination, page: current.pagination.page - 1 } }))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 font-semibold text-slate-600 disabled:opacity-40">Prev</button>
                  <button type="button" disabled={(history.pagination?.page || 1) >= (history.pagination?.totalPages || 1) || historyLoading} onClick={() => setHistory((current) => ({ ...current, pagination: { ...current.pagination, page: current.pagination.page + 1 } }))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 font-semibold text-slate-600 disabled:opacity-40">Next</button>
                </div>
              </div>
            </section>
          )}

          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
              <div className="flex w-full max-w-sm flex-col items-center rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center shadow-xl">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <p className="mt-4 text-sm font-semibold text-slate-900">Loading billing details</p>
                <p className="mt-1 text-xs text-slate-500">Preparing your subscription and add-on summary.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={showCancelAutoPay}
        onClose={() => setShowCancelAutoPay(false)}
        onConfirm={handleCancelAutoPay}
        title="Cancel card auto-pay?"
        zIndex={90}
        message="Future automatic subscription charges will stop. Your current paid access remains active until its end date."
        confirmText="Cancel Auto-pay"
        cancelText="Keep Auto-pay"
        type="delete"
        confirmDisabled={autoPayLoading}
      />
    </div>
  );
}
